// Publish only a completed native Registry form, never a click or invalid submit.
// No email, name or phone is stored here or included in the analytics event.
// Scope: native customer forms tagged olas_registry that are not the <olas-signup> form (today the
// dormant notify drawer and olas-registry-fallback). <olas-signup>, including its Shopify fallback,
// reports through olas-signup.js, so on the Registry page this script finds nothing to do.
// Consent: the same rule as olas-signup.js; an event is published only while
// Shopify.customerPrivacy.analyticsProcessingAllowed() is true.
const storageKey = 'olas_registry_attempt';
const maxAge = 10 * 60 * 1000;

export function confirmedSignup(attempt, formId, succeeded, now = Date.now()) {
  if (!attempt || !succeeded || attempt.formId !== formId ||
      !Number.isFinite(attempt.at) || now < attempt.at || now - attempt.at > maxAge ||
      !['registry_signup', 'release_request'].includes(attempt.kind)) return null;
  return { name: attempt.kind, data: {
    event_id: attempt.eventId,
    form_id: attempt.formId,
    work: attempt.work || '',
    variant_id: attempt.variantId || '',
    placement: attempt.placement,
    source: attempt.source,
  } };
}

export function analyticsAllowed(shopify) {
  try { return Boolean(shopify?.customerPrivacy?.analyticsProcessingAllowed?.()); } catch { return false; }
}

function installRegistryEvents() {
  const forms = [...document.forms].filter(form =>
    !form.hasAttribute('data-olas-signup') &&
    form.querySelector('[name="form_type"]')?.value === 'customer' &&
    form.querySelector('[name="contact[tags]"]')?.value.includes('olas_registry')
  );
  if (!forms.length) return;
  const knownSources = new Set(['instagram', 'facebook', 'tiktok', 'google', 'newsletter', 'email']);
  for (const form of forms) {
    form.addEventListener('submit', () => {
      if (!form.checkValidity()) return;
      const tags = form.querySelector('[name="contact[tags]"]').value.split(',');
      const source = new URLSearchParams(location.search).get('utm_source') ||
        new URLSearchParams((window.olasTrafficSource?.() || '').replaceAll(' ', '&')).get('utm_source');
      const attempt = {
        at: Date.now(), eventId: crypto.randomUUID(), formId: form.id,
        kind: tags.includes('olas_release') ? 'release_request' : 'registry_signup',
        placement: tags.includes('olas_release') ? 'product' : tags.includes('olas_footer') ? 'footer' : 'newsletter',
        work: tags.find(tag => tag.startsWith('olas_work_'))?.slice(10),
        variantId: tags.find(tag => tag.startsWith('olas_variant_'))?.slice(13),
        source: knownSources.has(source) ? source : source ? 'other' : 'direct',
      };
      try { sessionStorage.setItem(storageKey, JSON.stringify(attempt)); } catch { /* Signup still works without storage. */ }
    });
  }
  let attempt;
  try { attempt = JSON.parse(sessionStorage.getItem(storageKey)); } catch { return; }
  if (!attempt) return;
  const form = forms.find(form => form.id === attempt.formId);
  const event = confirmedSignup(attempt, form?.id, !!form?.querySelector('.form__message--success'));
  if (!event) return;
  // Customer events and the Customer Privacy API initialize asynchronously; never block the form.
  let retries = 0;
  const publish = () => {
    if (window.Shopify?.analytics?.publish && window.Shopify.customerPrivacy) {
      if (!analyticsAllowed(window.Shopify)) { try { sessionStorage.removeItem(storageKey); } catch { /* Nothing kept. */ } return; }
      try {
        if (window.Shopify.analytics.publish(event.name, event.data) !== false) {
          sessionStorage.removeItem(storageKey);
          return;
        }
      } catch { /* Keep the pending event until its short expiry. */ }
    }
    if (++retries < 20) setTimeout(publish, 500);
  };
  publish();
}
if (typeof document !== 'undefined') installRegistryEvents();
